import type { GatewayBrowserClient } from "../gateway.ts";
import type { SessionsListResult } from "../types.ts";
import { resolveDefaultModelRef, resolveSessionModelRef } from "../chat/chat-context-usage.ts";
import { sleepMs } from "../chat/chat-retry.ts";
import { sendChatMessage, type ChatState } from "./chat.ts";

export type ControlUiBuildInfo = {
  available: boolean;
  message?: string;
  repoRoot: string | null;
  indexPath: string | null;
  uiScriptPath: string | null;
  packageJsonPath: string | null;
  nodePath: string;
  pnpmPath: string | null;
  automatedArgv: string[];
  shell: {
    pnpmUiBuild: string;
    nodeUiBuild: string;
    cleanRestart: string;
    cdRepo: string;
  } | null;
};

export type DeveloperState = {
  client: GatewayBrowserClient | null;
  connected: boolean;
  devBuildInfoLoading: boolean;
  devBuildInfo: ControlUiBuildInfo | null;
  devBuildInfoError: string | null;
  devBuildRunning: boolean;
  devBuildActiveId: string | null;
  devBuildLines: string[];
  devBuildError: string | null;
  devBuildLastOk: boolean | null;
};

export type DevAiRebuildHost = DeveloperState &
  ChatState & {
    sessionsResult?: SessionsListResult | null;
  };

const AI_REBUILD_RUN_TIMEOUT_MS = 600_000;
const AI_REBUILD_OK_TOKEN = "CONTROL_UI_BUILD_OK";

export function buildControlUiAiRebuildPrompt(repoRoot: string | null): string {
  const root = repoRoot?.trim() || "the Jaisiu repository root on the gateway host";
  return `[control-ui-rebuild]
Rebuild the Control UI (Vite app under ui/).

1. cd to ${root} and run: pnpm ui:build
2. If the build fails, fix reported errors in ui/ (minimal diffs), then retry until success.
3. Do not edit .env files or gateway secrets.
4. When the build succeeds, end your reply with exactly: ${AI_REBUILD_OK_TOKEN}`;
}

export function resolveDevAiBuildModelLabel(
  sessionKey: string,
  sessions: SessionsListResult | null | undefined,
): string {
  const row = sessions?.sessions?.find((s) => s.key === sessionKey);
  const ref = resolveSessionModelRef(row, sessions?.defaults);
  if (ref) {
    return ref;
  }
  return resolveDefaultModelRef(sessions?.defaults) ?? "session default";
}

export function reloadControlUiPage(): void {
  if (typeof window !== "undefined") {
    window.location.reload();
  }
}

export async function waitForChatRunToFinish(
  state: Pick<ChatState, "chatRunId">,
  runId: string,
  timeoutMs = AI_REBUILD_RUN_TIMEOUT_MS,
): Promise<boolean> {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    if (state.chatRunId !== runId) {
      return true;
    }
    await sleepMs(400);
  }
  return false;
}

export type ControlUiBuildEventPayload = {
  phase?: string;
  buildId?: string;
  stream?: string;
  text?: string;
  message?: string;
  repoRoot?: string;
  command?: string[];
  durationMs?: number;
  indexPath?: string;
  ok?: boolean;
};

function formatCommand(command: string[] | undefined): string {
  if (!command?.length) {
    return "$ node scripts/ui.js build";
  }
  return `$ ${command.join(" ")}`;
}

export function clearDevBuildOutput(state: DeveloperState): void {
  state.devBuildLines = [];
  state.devBuildError = null;
  state.devBuildLastOk = null;
}

export async function loadDevBuildInfo(state: DeveloperState): Promise<void> {
  if (!state.client || !state.connected) {
    return;
  }
  if (state.devBuildInfoLoading) {
    return;
  }
  state.devBuildInfoLoading = true;
  state.devBuildInfoError = null;
  try {
    const info = (await state.client.request("controlUi.buildInfo", {})) as ControlUiBuildInfo;
    state.devBuildInfo = info;
  } catch (err) {
    const message = String(err);
    if (message.includes("unknown method: controlUi.buildInfo")) {
      state.devBuildInfoError =
        "Gateway is running an older build without controlUi.buildInfo. On the gateway host run: pnpm build && node openclaw.mjs daemon restart";
    } else {
      state.devBuildInfoError = message;
    }
    state.devBuildInfo = null;
  } finally {
    state.devBuildInfoLoading = false;
  }
}

export function handleControlUiBuildEvent(
  state: DeveloperState,
  payload: ControlUiBuildEventPayload | undefined,
): void {
  if (!payload || typeof payload !== "object") {
    return;
  }
  const buildId = typeof payload.buildId === "string" ? payload.buildId : null;
  if (buildId && state.devBuildActiveId && buildId !== state.devBuildActiveId) {
    return;
  }

  if (payload.phase === "start") {
    state.devBuildLines = [formatCommand(payload.command), ""];
    state.devBuildError = null;
    return;
  }

  if (payload.phase === "line" && typeof payload.text === "string") {
    const prefix = payload.stream === "stderr" ? "[stderr] " : "";
    state.devBuildLines = [...state.devBuildLines, `${prefix}${payload.text}`];
    return;
  }

  if (payload.phase === "error" && typeof payload.message === "string") {
    state.devBuildError = payload.message;
    state.devBuildLastOk = false;
    state.devBuildLines = [...state.devBuildLines, "", `Error: ${payload.message}`];
    return;
  }

  if (payload.phase === "done") {
    state.devBuildLastOk = payload.ok === true;
    const duration = typeof payload.durationMs === "number" ? ` (${payload.durationMs}ms)` : "";
    const indexPath = typeof payload.indexPath === "string" ? payload.indexPath : "";
    state.devBuildLines = [
      ...state.devBuildLines,
      "",
      payload.ok ? `Build succeeded${duration}.` : "Build failed.",
      ...(indexPath ? [`Output: ${indexPath}`] : []),
    ];
  }
}

async function executeDevControlUiBuild(state: DeveloperState): Promise<void> {
  if (!state.client || !state.connected) {
    return;
  }

  const buildId = crypto.randomUUID();
  state.devBuildActiveId = buildId;
  state.devBuildError = null;
  state.devBuildLastOk = null;
  const shellCmd = state.devBuildInfo?.shell?.pnpmUiBuild;
  if (!state.devBuildLines.some((line) => line.startsWith("#") || line.startsWith("Agent"))) {
    state.devBuildLines = [shellCmd ? `# ${shellCmd}` : "Starting Control UI build…", ""];
  }

  try {
    const result = (await state.client.request("controlUi.build", { buildId })) as {
      ok?: boolean;
      durationMs?: number;
      indexPath?: string;
    };
    state.devBuildLastOk = result?.ok === true;
    if (result?.ok) {
      const duration = typeof result.durationMs === "number" ? ` (${result.durationMs}ms)` : "";
      if (!state.devBuildLines.some((line) => line.startsWith("Build succeeded"))) {
        state.devBuildLines = [
          ...state.devBuildLines,
          "",
          `Build succeeded${duration}.`,
          ...(result.indexPath ? [`Output: ${result.indexPath}`] : []),
        ];
      }
      void loadDevBuildInfo(state);
    }
  } catch (err) {
    state.devBuildError = String(err);
    state.devBuildLastOk = false;
    if (!state.devBuildLines.some((line) => line.startsWith("Error:"))) {
      state.devBuildLines = [...state.devBuildLines, "", `Error: ${state.devBuildError}`];
    }
  }
}

export async function runDevControlUiBuild(state: DeveloperState): Promise<void> {
  if (!state.client || !state.connected || state.devBuildRunning) {
    return;
  }
  state.devBuildRunning = true;
  try {
    await executeDevControlUiBuild(state);
  } finally {
    state.devBuildRunning = false;
  }
}

export async function runDevCleanRestart(state: DeveloperState): Promise<void> {
  if (!state.client || !state.connected || state.devBuildRunning) {
    return;
  }
  if (!state.devBuildInfo?.available) {
    state.devBuildError =
      "Clean restart requires a Jaisiu checkout on the gateway host. Refresh paths first.";
    return;
  }

  state.devBuildRunning = true;
  state.devBuildError = null;
  state.devBuildLastOk = null;
  const shellCmd = state.devBuildInfo.shell?.cleanRestart;
  state.devBuildLines = [
    shellCmd ? `# ${shellCmd}` : "# Clean restart",
    "",
    "Stopping gateway, rebuilding, and starting again…",
    "This tab will disconnect when the gateway stops.",
    "",
  ];

  try {
    const result = (await state.client.request("controlUi.cleanRestart", {})) as {
      ok?: boolean;
      message?: string;
      logPath?: string;
      pid?: number | null;
    };
    if (result?.ok) {
      state.devBuildLastOk = true;
      state.devBuildLines = [
        ...state.devBuildLines,
        result.message ?? "Clean restart started.",
        ...(result.logPath ? [`Log: ${result.logPath}`] : []),
        ...(typeof result.pid === "number" ? [`Helper pid: ${result.pid}`] : []),
        "",
        "Hard-refresh this page once the gateway is listening again (~30s).",
      ];
    } else {
      state.devBuildLastOk = false;
      state.devBuildError = "Clean restart did not start.";
    }
  } catch (err) {
    const message = String(err);
    if (message.includes("unknown method: controlUi.cleanRestart")) {
      state.devBuildError =
        "Gateway is running an older build without controlUi.cleanRestart. Run scripts/jaisiu-clean-restart.sh on the host or rebuild the gateway.";
    } else {
      state.devBuildError = message;
    }
    state.devBuildLastOk = false;
    state.devBuildLines = [...state.devBuildLines, "", `Error: ${state.devBuildError}`];
  } finally {
    state.devBuildRunning = false;
  }
}

export async function runDevControlUiBuildAndReload(state: DeveloperState): Promise<void> {
  await runDevControlUiBuild(state);
  if (state.devBuildLastOk) {
    reloadControlUiPage();
  }
}

export async function runDevControlUiAiBuildAndReload(host: DevAiRebuildHost): Promise<void> {
  if (!host.client || !host.connected || host.devBuildRunning) {
    return;
  }
  if (!host.devBuildInfo?.available) {
    host.devBuildError =
      "AI rebuild requires a Jaisiu checkout on the gateway host. Refresh paths or use shell commands.";
    return;
  }

  const sessionKey = host.sessionKey.trim();
  if (!sessionKey) {
    host.devBuildError = "Open a chat session first (model comes from the chat Model picker).";
    return;
  }

  const modelLabel = resolveDevAiBuildModelLabel(sessionKey, host.sessionsResult);
  const prompt = buildControlUiAiRebuildPrompt(host.devBuildInfo.repoRoot);

  host.devBuildRunning = true;
  host.devBuildError = null;
  host.devBuildLastOk = null;
  host.devBuildLines = [
    `# AI rebuild via chat (${modelLabel})`,
    `# Session: ${sessionKey}`,
    "",
    "Sending rebuild task to the agent…",
    "",
  ];

  try {
    const runId = await sendChatMessage(host, prompt);
    if (!runId) {
      host.devBuildError = host.lastError ?? "Failed to start agent run for AI rebuild.";
      host.devBuildLastOk = false;
      return;
    }

    host.devBuildLines = [
      ...host.devBuildLines,
      `Agent run ${runId.slice(0, 8)}… (waiting for reply)`,
      "",
    ];

    const finished = await waitForChatRunToFinish(host, runId);
    if (!finished) {
      host.devBuildError = "Agent run timed out before finishing the rebuild task.";
      host.devBuildLastOk = false;
      return;
    }

    host.devBuildLines = [...host.devBuildLines, "Agent finished. Running gateway ui:build…", ""];
    await executeDevControlUiBuild(host);
    if (host.devBuildLastOk) {
      host.devBuildLines = [...host.devBuildLines, "", "Reloading page…"];
      reloadControlUiPage();
    }
  } catch (err) {
    host.devBuildError = String(err);
    host.devBuildLastOk = false;
    host.devBuildLines = [...host.devBuildLines, "", `Error: ${host.devBuildError}`];
  } finally {
    host.devBuildRunning = false;
  }
}
